import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, ErrorMessage, Field, Formik } from "formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import { addFaqSchema } from "components/Common/validation"
import { addFaq, getFaqDetails, updateFaq } from "store/actions/faqAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  title: "",
  titleAr: "",
  description: "",
  descriptionAr: "",
}

const AddFaq = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"Faq | Namq"}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      18,
      "faq_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      18,
      "faq_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  const { loading, isLoading, faqDetails } = useSelector(state => state.Faqs)
  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    if (mode === "edit") {
      fetchFaqDetails()
    }
  }, [])

  const fetchFaqDetails = () => {
    const payload = { id }
    dispatch(getFaqDetails(payload))
  }

  useEffect(() => {
    if (faqDetails && !loading && mode === "edit") {
      const { title, titleAr, description, descriptionAr } = faqDetails
      //   console.log(taxInfo)
      setFormValues({
        ...formValues,
        title,
        titleAr,
        description,
        descriptionAr,
      })
    }
  }, [faqDetails])

  const handleOnSubmit = values => {
    // const { title, titleAr, description, descriptionAr } = values
    if (mode === "add") {
      dispatch(addFaq(values))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/faq`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...values,
        id,
      }
      dispatch(updateFaq(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {"FAQ"}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addFaqSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="title">
                              <span className="text-danger">*</span>{" "}
                              {t("Title(English)")}
                            </label>
                            <Field
                              type="text"
                              id="title"
                              name="title"
                              placeholder={t("Title(English)")}
                              className={`form-control ${
                                touched.title && errors.title
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="title" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="titleAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Title(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="titleAr"
                              name="titleAr"
                              placeholder={t("Title(عربي)")}
                              className={`form-control ${
                                touched.titleAr && errors.titleAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="titleAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              {t("Description(English)")}
                            </label>
                            <Field
                              as="textarea"
                              id="description"
                              name="description"
                              placeholder={t("Description(English)")}
                              className={`form-control`}
                            />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              {t("Description(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              id="descriptionAr"
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              style={{ direction: "rtl" }}
                              className={`form-control ${
                                touched.descriptionAr && errors.descriptionAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="descriptionAr" />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end mt-3 mb-1">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddFaq
